import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";

export default function TestimonialCarousel({ featured = "home" }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/testimonials?featured=${featured}`).then((r) => setItems(r.data)).catch(() => {});
  }, [featured]);

  useEffect(() => {
    if (items.length < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const t = items[idx];

  return (
    <section className="py-20 md:py-28">
      <div className="container-page">
        <div className="max-w-2xl mb-10">
          <span className="eyebrow">Testimonials</span>
          <h2 className="mt-4 section-title">Trusted by teams that care about security.</h2>
        </div>

        <div className="relative">
          <div className="card-elevated p-10 md:p-14 relative overflow-hidden">
            <Quote className="absolute top-8 right-8 h-16 w-16 text-[#1565FF]/10 dark:text-[#4FC3F7]/10" strokeWidth={1.5} />

            <div key={t.id} className="animate-fade-up">
              <div className="flex items-center gap-1 mb-4" data-testid={`testimonial-rating-${t.id}`}>
                {Array.from({ length: t.rating || 5 }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#1565FF] text-[#1565FF] dark:fill-[#4FC3F7] dark:text-[#4FC3F7]" />
                ))}
              </div>
              <blockquote className="text-xl md:text-2xl font-medium tracking-tight leading-relaxed">
                "{t.quote}"
              </blockquote>
              <div className="mt-6 flex items-center gap-4">
                {t.photo_url ? (
                  <img src={t.photo_url} alt={t.name} className="h-12 w-12 rounded-full object-cover border border-black/10 dark:border-white/10" />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] grid place-items-center text-white font-semibold">
                    {(t.name || "?").split(" ").map(s => s[0]).join("").slice(0,2)}
                  </div>
                )}
                <div>
                  <div className="text-sm font-semibold tracking-tight">{t.name}</div>
                  <div className="text-xs text-muted-foreground">{[t.role, t.company].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            </div>

            {items.length > 1 && (
              <div className="mt-8 flex items-center justify-between">
                <div className="flex gap-1.5" data-testid="testimonial-dots">
                  {items.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      aria-label={`Testimonial ${i + 1}`}
                      data-testid={`testimonial-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#1565FF] dark:bg-[#4FC3F7]" : "w-1.5 bg-black/20 dark:bg-white/20"}`}
                    />
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
                    aria-label="Previous"
                    data-testid="testimonial-prev"
                    className="grid h-9 w-9 place-items-center rounded-full border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setIdx((i) => (i + 1) % items.length)}
                    aria-label="Next"
                    data-testid="testimonial-next"
                    className="grid h-9 w-9 place-items-center rounded-full border border-black/10 dark:border-white/10 hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
